import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Calendar,
  Check,
  ExternalLink,
  FileText,
  Pencil,
  RefreshCw,
  Send,
  Sparkles,
  Video,
  X,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { todayPanelTz } from "@axorial/shared";
import type {
  CalendarEventRow,
  EventSession,
  EventSessionAttachment,
  EventSessionStatus,
  SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/events/$eventId/sessions")({
  component: SessionsListPage,
});

interface ListItem {
  occurrenceDate: string;
  occurrenceEndDate: string;
  isPast: boolean;
  isToday: boolean;
  session: EventSession | null;
  status: EventSessionStatus;
}

interface ListResponse {
  event: CalendarEventRow;
  items: ListItem[];
}

const STATUS_LABEL: Record<EventSessionStatus, string> = {
  planned: "Planeado",
  completed: "Realizado",
  cancelled: "Cancelado",
  skipped: "No se tuvo",
};

const STATUS_BADGE: Record<EventSessionStatus, string> = {
  planned: "badge-ghost",
  completed: "badge-success",
  cancelled: "badge-error",
  skipped: "badge-warning",
};

function SessionsListPage() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ["event-sessions", eventId],
    queryFn: () => api.get(`/event-sessions/${eventId}`),
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Default selected = próxima ocurrencia (hoy o futura) para que el
  // equipo entre directo a la sesión que tienen que preparar. Si no hay
  // próximas (serie acabada), fallback a la última pasada.
  useEffect(() => {
    if (selectedDate || !data) return;
    const todayIso = todayPanelTz();
    const upcoming = data.items.filter((i) => i.occurrenceDate >= todayIso);
    const past = data.items.filter((i) => i.occurrenceDate < todayIso);
    const candidate = upcoming[0] ?? past[past.length - 1];
    if (candidate) setSelectedDate(candidate.occurrenceDate);
  }, [data, selectedDate]);

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  // Partición temporal: Pasadas (< hoy) y Próximas (hoy en adelante).
  // Pasadas se ordena descendente (más reciente primero); Próximas
  // ascendente (la siguiente arriba).
  const todayIso = todayPanelTz();
  const past = data.items
    .filter((i) => i.occurrenceDate < todayIso)
    .toSorted((a, b) => b.occurrenceDate.localeCompare(a.occurrenceDate));
  const upcoming = data.items
    .filter((i) => i.occurrenceDate >= todayIso)
    .toSorted((a, b) => a.occurrenceDate.localeCompare(b.occurrenceDate));

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Sesiones"
        title={data.event.name}
        subtitle={
          data.event.recurrence
            ? `Recurrente — ${data.event.recurrence} · histórico y próximas`
            : "Evento único"
        }
        actions={
          <Link to="/calendar" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Calendario
          </Link>
        }
      />

      <div className="space-y-4">
        {/* Tira horizontal de ocurrencias con tabs Pasadas / Próximas.
            Antes era una columna izquierda fija de 320px que dejaba mucho
            espacio muerto bajo el último ítem; mover a un bar arriba libera
            el ancho completo para el detalle (iframes + artifacts). */}
        <OccurrencesBar
          past={past}
          upcoming={upcoming}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
        />

        {/* Detalle de la sesión seleccionada */}
        <div className="min-h-0">
          {selectedDate ? (
            <SessionDetail
              eventId={eventId}
              occurrenceDate={selectedDate}
              event={data.event}
              onDeleted={() => {
                // Refresca la lista para reflejar el cambio de status.
                navigate({ to: ".", search: {}, replace: true });
              }}
            />
          ) : (
            <div className="card-elevated p-8 text-center text-base-content/55">
              Selecciona una sesión del listado.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface DetailResponse {
  event: CalendarEventRow;
  session: EventSession | null;
  attachments: EventSessionAttachment[];
}

function SessionDetail({
  eventId,
  occurrenceDate,
  event,
  onDeleted,
}: {
  eventId: string;
  occurrenceDate: string;
  event: CalendarEventRow;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const { data: detail, isLoading } = useQuery<DetailResponse>({
    queryKey: ["event-session", eventId, occurrenceDate],
    queryFn: () => api.get(`/event-sessions/${eventId}/${occurrenceDate}`),
  });
  const { data: me } = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });

  const [description, setDescription] = useState("");
  const [muralUrl, setMuralUrl] = useState("");
  const [muralEmbedUrl, setMuralEmbedUrl] = useState("");
  const [recordingUrl, setRecordingUrl] = useState("");
  const [transcriptUrl, setTranscriptUrl] = useState("");
  const [geminiNotesUrl, setGeminiNotesUrl] = useState("");
  const [status, setStatus] = useState<EventSessionStatus>("planned");
  const [savedFeedback, setSavedFeedback] = useState<string | null>(null);
  // Vista por default = read-only. Sólo admin/lead pueden alternar a edit.
  // Cualquier cambio de ocurrencia reinicia a view (no querés "quedarte"
  // editando si saltás de fecha sin guardar).
  const [mode, setMode] = useState<"view" | "edit">("view");

  // Prefill desde server. Solo en el primer load por (eventId, occurrenceDate)
  // para no pisar mientras el usuario tipea. IMPORTANTE: la marca de
  // "ya prefilled" se setea DESPUÉS de aplicar los valores — si el detail
  // todavía está cargando, salimos sin marcar y el efecto re-corre cuando
  // detail llegue (sino veríamos los datos viejos de la sesión anterior).
  const prefilledFor = useRef<string>("");
  useEffect(() => {
    const key = `${eventId}:${occurrenceDate}`;
    if (prefilledFor.current === key) return;
    if (!detail) return;
    setDescription(detail.session?.description ?? "");
    setMuralUrl(detail.session?.muralUrl ?? "");
    setMuralEmbedUrl(detail.session?.muralEmbedUrl ?? "");
    setRecordingUrl(detail.session?.recordingUrl ?? "");
    setTranscriptUrl(detail.session?.transcriptUrl ?? "");
    setGeminiNotesUrl(detail.session?.geminiNotesUrl ?? "");
    setStatus(detail.session?.status ?? "completed");
    setMode("view");
    prefilledFor.current = key;
  }, [detail, eventId, occurrenceDate]);

  const canEdit = me?.role === "admin" || me?.role === "lead";

  const fetchArtifacts = useMutation({
    // force=1: cuando el user clickea "Buscar artefactos" siempre
    // queremos un retry fresco contra Google, sin el back-off del back.
    // El auto-trigger (llamado desde useEffect en page load) NO lo
    // setea para evitar martillar la API en cada navegación.
    mutationFn: (opts?: { force?: boolean }) =>
      api.post<{
        ok: boolean;
        fromCache: boolean;
        recordingUrl: string | null;
        transcriptUrl: string | null;
        geminiNotesUrl: string | null;
      }>(
        `/event-sessions/${eventId}/${occurrenceDate}/fetch-artifacts${opts?.force ? "?force=1" : ""}`,
      ),
    onSuccess: (res) => {
      // Actualizamos el state local directamente con lo que vino. El
      // prefill effect tiene una flag prefilledFor que NO re-corre tras
      // invalidation (a propósito, para no pisar mientras el user edita),
      // así que sin esto el botón "Gemini" tarda en aparecer hasta que
      // el user navega o recarga.
      if (res.recordingUrl) setRecordingUrl(res.recordingUrl);
      if (res.transcriptUrl) setTranscriptUrl(res.transcriptUrl);
      if (res.geminiNotesUrl) setGeminiNotesUrl(res.geminiNotesUrl);
      // Igual invalidamos por si alguna otra vista del detalle depende
      // del query (status auto-completado, attachments, etc).
      qc.invalidateQueries({ queryKey: ["event-session", eventId, occurrenceDate] });
      qc.invalidateQueries({ queryKey: ["event-sessions", eventId] });
      if (!res.fromCache) {
        const found = [
          res.recordingUrl && "grabación",
          res.transcriptUrl && "transcripción",
          res.geminiNotesUrl && "notas Gemini",
        ].filter(Boolean);
        setSavedFeedback(found.length ? `Encontrado: ${found.join(", ")}` : "Sin artefactos aún");
        setTimeout(() => setSavedFeedback(null), 3500);
      }
    },
  });

  // Auto-trigger: si esta es una ocurrencia pasada (ayer en adelante atrás),
  // dentro de los últimos 14 días, y le falta al menos un artefacto, hacemos
  // el fetch una vez en silencio. El back-off del backend evita spam si la
  // junta aún no se procesó.
  const autoTriggeredRef = useRef<string>("");
  useEffect(() => {
    const key = `${eventId}:${occurrenceDate}`;
    if (autoTriggeredRef.current === key) return;
    if (!detail || !event.externalMeetUrl) return;
    const todayIso = todayPanelTz();
    const occDate = new Date(occurrenceDate);
    const today = new Date(todayIso);
    const daysSince = Math.floor((today.getTime() - occDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince < 0 || daysSince > 14) return;
    const missing =
      !detail.session?.recordingUrl ||
      !detail.session?.transcriptUrl ||
      !detail.session?.geminiNotesUrl;
    if (!missing) return;
    autoTriggeredRef.current = key;
    // Auto-trigger: respeta el back-off del backend (no force).
    fetchArtifacts.mutate(undefined);
  }, [detail, event.externalMeetUrl, eventId, occurrenceDate, fetchArtifacts]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/event-sessions/${eventId}/${occurrenceDate}`, {
        description: description || null,
        muralUrl: muralUrl || null,
        // El usuario puede pegar el `<iframe>` completo. Normalizamos al
        // src= antes de mandar — el backend valida url() puro.
        muralEmbedUrl: extractIframeSrc(muralEmbedUrl) ?? null,
        recordingUrl: recordingUrl || null,
        transcriptUrl: transcriptUrl || null,
        geminiNotesUrl: geminiNotesUrl || null,
        status,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-sessions", eventId] });
      qc.invalidateQueries({ queryKey: ["event-session", eventId, occurrenceDate] });
      setSavedFeedback("Guardado");
      setTimeout(() => setSavedFeedback(null), 2000);
      setMode("view");
    },
  });

  const publish = useMutation({
    mutationFn: () => api.post(`/event-sessions/${eventId}/${occurrenceDate}/publish`),
    onSuccess: () => {
      setSavedFeedback("Publicado a Google");
      setTimeout(() => setSavedFeedback(null), 2500);
    },
  });

  const del = useMutation({
    mutationFn: () => api.del(`/event-sessions/${eventId}/${occurrenceDate}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-sessions", eventId] });
      qc.invalidateQueries({ queryKey: ["event-session", eventId, occurrenceDate] });
      onDeleted();
    },
  });

  const canPublishToGoogle =
    event.source === "google_calendar" && event.userId === me?.id && !!event.externalId;
  // El embed siempre viene del campo dedicado `muralEmbedUrl`. Si el usuario
  // pegó el `<iframe ...>` completo, extraemos el `src=`. Si no hay nada en
  // ese campo, intentamos derivar del muralUrl como fallback (sólo funciona
  // si Mural mantuviera misma ID share=embed, hoy no es el caso).
  const muralEmbed = useMemo(
    () => extractIframeSrc(muralEmbedUrl) ?? toMuralEmbed(muralUrl),
    [muralEmbedUrl, muralUrl],
  );

  if (isLoading || !detail) {
    return (
      <div className="card-elevated p-8 grid place-items-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="card-elevated p-5 space-y-4">
      <header className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-lg leading-snug">
            {formatDate(occurrenceDate)}
          </h2>
          <p className="text-xs text-base-content/55 mt-0.5">
            {event.name} ·{" "}
            {detail.session?.lastEditedById ? "última edición guardada" : "sin notas todavía"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {mode === "view" ? (
            <>
              <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  className="btn btn-ghost btn-sm"
                >
                  <Pencil className="size-4" /> Editar
                </button>
              )}
            </>
          ) : (
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as EventSessionStatus)}
              className="select select-bordered select-sm"
            >
              <option value="planned">Planeado</option>
              <option value="completed">Realizado</option>
              <option value="cancelled">Cancelado</option>
              <option value="skipped">No se tuvo</option>
            </select>
          )}
        </div>
      </header>

      {mode === "view" ? (
        <ViewBody
          description={description}
          muralUrl={muralUrl}
          muralEmbed={muralEmbed}
          recordingUrl={recordingUrl}
          transcriptUrl={transcriptUrl}
          geminiNotesUrl={geminiNotesUrl}
          attachments={detail.attachments}
        />
      ) : (
        <EditBody
          eventId={eventId}
          occurrenceDate={occurrenceDate}
          description={description}
          setDescription={setDescription}
          muralUrl={muralUrl}
          setMuralUrl={setMuralUrl}
          muralEmbedUrl={muralEmbedUrl}
          setMuralEmbedUrl={setMuralEmbedUrl}
          muralEmbed={muralEmbed}
          recordingUrl={recordingUrl}
          setRecordingUrl={setRecordingUrl}
          transcriptUrl={transcriptUrl}
          setTranscriptUrl={setTranscriptUrl}
          geminiNotesUrl={geminiNotesUrl}
          setGeminiNotesUrl={setGeminiNotesUrl}
          attachments={detail.attachments}
        />
      )}

      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-base-300/40">
        {mode === "edit" ? (
          <>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn btn-primary btn-sm"
            >
              {save.isPending && <span className="loading loading-spinner loading-xs" />}
              <Check className="size-4" /> Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                // Cancela edición: restaura los valores originales del server.
                if (detail.session) {
                  setDescription(detail.session.description ?? "");
                  setMuralUrl(detail.session.muralUrl ?? "");
                  setMuralEmbedUrl(detail.session.muralEmbedUrl ?? "");
                  setRecordingUrl(detail.session.recordingUrl ?? "");
                  setTranscriptUrl(detail.session.transcriptUrl ?? "");
                  setGeminiNotesUrl(detail.session.geminiNotesUrl ?? "");
                  setStatus(detail.session.status);
                } else {
                  setDescription("");
                  setMuralUrl("");
                  setMuralEmbedUrl("");
                  setRecordingUrl("");
                  setTranscriptUrl("");
                  setGeminiNotesUrl("");
                  setStatus("completed");
                }
                setMode("view");
              }}
              className="btn btn-ghost btn-sm"
            >
              Cancelar
            </button>
            {canPublishToGoogle && (
              <button
                type="button"
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className="btn btn-ghost btn-sm"
                title="Pushea la descripción de la sesión al evento en Google Calendar"
              >
                {publish.isPending && <span className="loading loading-spinner loading-xs" />}
                <Send className="size-4" /> Publicar a Google
              </button>
            )}
            {detail.session && canEdit && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("¿Borrar la sesión? Las notas + adjuntos se pierden.")) {
                    del.mutate();
                  }
                }}
                className="btn btn-ghost btn-sm text-error ml-auto"
              >
                <Trash2 className="size-4" /> Borrar sesión
              </button>
            )}
          </>
        ) : (
          <>
            {recordingUrl && (
              <a
                href={recordingUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-ghost btn-sm"
              >
                <Video className="size-4" /> Grabación
              </a>
            )}
            {transcriptUrl && (
              <a
                href={transcriptUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-ghost btn-sm"
              >
                <FileText className="size-4" /> Transcripción
              </a>
            )}
            {geminiNotesUrl && (
              <a
                href={geminiNotesUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-ghost btn-sm"
              >
                <Sparkles className="size-4" /> Notas (Gemini)
              </a>
            )}
            {event.externalMeetUrl && (
              <button
                type="button"
                onClick={() => fetchArtifacts.mutate({ force: true })}
                disabled={fetchArtifacts.isPending}
                className="btn btn-ghost btn-sm ml-auto"
                title="Vuelve a buscar grabación/transcripción/notas en Google Meet"
              >
                {fetchArtifacts.isPending ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                Buscar artefactos
              </button>
            )}
          </>
        )}
        {savedFeedback && <span className="text-xs text-success ml-2">✓ {savedFeedback}</span>}
      </div>
    </div>
  );
}

function ViewBody({
  description,
  muralUrl,
  muralEmbed,
  recordingUrl,
  transcriptUrl,
  geminiNotesUrl,
  attachments,
}: {
  description: string;
  muralUrl: string;
  muralEmbed: string | null;
  recordingUrl: string;
  transcriptUrl: string;
  geminiNotesUrl: string;
  attachments: EventSessionAttachment[];
}) {
  const hasArtifacts = recordingUrl || transcriptUrl || geminiNotesUrl;
  const hasContent = description || muralUrl || hasArtifacts || attachments.length > 0;
  if (!hasContent) {
    return (
      <p className="text-sm text-base-content/55 italic py-4">
        Esta sesión aún no tiene contenido registrado.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {description ? (
        <section>
          <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5">
            Descripción / notas
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap rounded-md border border-base-300/40 bg-base-200/20 p-3">
            {description}
          </div>
        </section>
      ) : null}

      {/* Mural + Artefactos de Meet: cuando ambos existen los pintamos
          lado a lado en xl+ para aprovechar el ancho. En lg- se apilan.
          Altura uniforme entre los dos iframes para no romper el grid. */}
      {(muralEmbed || muralUrl || hasArtifacts) && (
        <div
          className={
            muralEmbed && hasArtifacts ? "grid grid-cols-1 xl:grid-cols-2 gap-4" : "space-y-4"
          }
        >
          {muralEmbed ? (
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 flex items-center gap-2">
                <span>Mural</span>
                <a
                  href={muralUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary inline-flex items-center gap-1 hover:underline normal-case"
                >
                  abrir <ExternalLink className="size-3" />
                </a>
              </div>
              <div className="rounded-md overflow-hidden border border-base-300/60">
                <iframe
                  src={muralEmbed}
                  title="Mural embed"
                  className="w-full"
                  style={{ height: muralEmbed && hasArtifacts ? "520px" : "500px" }}
                  allow="fullscreen"
                />
              </div>
            </section>
          ) : muralUrl ? (
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5">
                Mural
              </div>
              <a
                href={muralUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
              >
                {muralUrl} <ExternalLink className="size-3" />
              </a>
            </section>
          ) : null}

          {hasArtifacts && (
            <ArtifactsTabs
              recordingUrl={recordingUrl}
              transcriptUrl={transcriptUrl}
              geminiNotesUrl={geminiNotesUrl}
              compact={!!muralEmbed}
            />
          )}
        </div>
      )}

      {attachments.length > 0 && (
        <section>
          <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 flex items-center gap-1.5">
            <Paperclip className="size-3" /> Adjuntos
            <span className="badge badge-ghost badge-xs">{attachments.length}</span>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="relative rounded border border-base-300/60 overflow-hidden bg-base-100"
              >
                <a
                  href={`/api/event-sessions/attachments/${a.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {a.mimeType.startsWith("image/") ? (
                    <img
                      src={`/api/event-sessions/attachments/${a.id}`}
                      alt={a.filename}
                      className="w-full aspect-video object-cover"
                    />
                  ) : (
                    <div className="aspect-video grid place-items-center text-xs text-base-content/55 p-2">
                      <ExternalLink className="size-4 opacity-50" />
                    </div>
                  )}
                </a>
                <div className="px-1.5 py-0.5 text-[10px] truncate text-base-content/55">
                  {a.filename}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function EditBody({
  eventId,
  occurrenceDate,
  description,
  setDescription,
  muralUrl,
  setMuralUrl,
  muralEmbedUrl,
  setMuralEmbedUrl,
  muralEmbed,
  recordingUrl,
  setRecordingUrl,
  transcriptUrl,
  setTranscriptUrl,
  geminiNotesUrl,
  setGeminiNotesUrl,
  attachments,
}: {
  eventId: string;
  occurrenceDate: string;
  description: string;
  setDescription: (v: string) => void;
  muralUrl: string;
  setMuralUrl: (v: string) => void;
  muralEmbedUrl: string;
  setMuralEmbedUrl: (v: string) => void;
  muralEmbed: string | null;
  recordingUrl: string;
  setRecordingUrl: (v: string) => void;
  transcriptUrl: string;
  setTranscriptUrl: (v: string) => void;
  geminiNotesUrl: string;
  setGeminiNotesUrl: (v: string) => void;
  attachments: EventSessionAttachment[];
}) {
  return (
    <>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
          Descripción / notas
        </label>
        <textarea
          rows={8}
          className="textarea textarea-bordered w-full font-mono text-sm leading-relaxed"
          placeholder="Resumen de lo discutido, decisiones, action items... acepta markdown."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
            Link a Mural (compartir)
          </label>
          <input
            type="url"
            className="input input-bordered input-sm w-full"
            placeholder="https://app.mural.co/t/.../m/..."
            value={muralUrl}
            onChange={(e) => setMuralUrl(e.target.value)}
          />
          <p className="text-xs text-base-content/55 mt-1">
            El que se copia desde el botón "Share" en Mural. Es el link al que se va el usuario
            cuando clickea "Abrir".
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
            Embed de Mural
          </label>
          <textarea
            rows={2}
            className="textarea textarea-bordered textarea-sm w-full font-mono text-xs"
            placeholder="Pegá el <iframe ...> completo o solo https://app.mural.co/embed/..."
            value={muralEmbedUrl}
            onChange={(e) => setMuralEmbedUrl(e.target.value)}
          />
          <p className="text-xs text-base-content/55 mt-1">
            Mural usa IDs distintos para share vs embed. Sacalo de "Open in new tab" → "Embed" o del
            HTML directo (auto-extraemos el src).
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
            Link de grabación
          </label>
          <input
            type="url"
            className="input input-bordered input-sm w-full"
            placeholder="https://drive.google.com/..."
            value={recordingUrl}
            onChange={(e) => setRecordingUrl(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
            Link de transcripción
          </label>
          <input
            type="url"
            className="input input-bordered input-sm w-full"
            placeholder="https://docs.google.com/..."
            value={transcriptUrl}
            onChange={(e) => setTranscriptUrl(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-1.5 block">
            Notas (Gemini)
          </label>
          <input
            type="url"
            className="input input-bordered input-sm w-full"
            placeholder="https://docs.google.com/..."
            value={geminiNotesUrl}
            onChange={(e) => setGeminiNotesUrl(e.target.value)}
          />
        </div>
      </div>

      {muralEmbed && (
        <div className="rounded-md overflow-hidden border border-base-300/60">
          <iframe
            src={muralEmbed}
            title="Mural embed"
            className="w-full"
            style={{ height: "500px" }}
            allow="fullscreen"
          />
        </div>
      )}

      <Attachments eventId={eventId} occurrenceDate={occurrenceDate} attachments={attachments} />
    </>
  );
}

function Attachments({
  eventId,
  occurrenceDate,
  attachments,
}: {
  eventId: string;
  occurrenceDate: string;
  attachments: EventSessionAttachment[];
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files: FileList) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/event-sessions/${eventId}/${occurrenceDate}/attachments`, form);
      }
      qc.invalidateQueries({ queryKey: ["event-session", eventId, occurrenceDate] });
    } finally {
      setUploading(false);
    }
  };

  const del = useMutation({
    mutationFn: (attId: string) => api.del(`/event-sessions/attachments/${attId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-session", eventId, occurrenceDate] });
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-base-content/65 flex items-center gap-1.5">
          <Paperclip className="size-3" /> Adjuntos
          {attachments.length > 0 && (
            <span className="badge badge-ghost badge-xs">{attachments.length}</span>
          )}
        </label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn btn-ghost btn-xs"
        >
          {uploading ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <Upload className="size-3" />
          )}
          Subir
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) upload(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-base-content/45 italic">Sin adjuntos.</p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="relative rounded border border-base-300/60 overflow-hidden bg-base-100"
            >
              <a href={`/api/event-sessions/attachments/${a.id}`} target="_blank" rel="noreferrer">
                {a.mimeType.startsWith("image/") ? (
                  <img
                    src={`/api/event-sessions/attachments/${a.id}`}
                    alt={a.filename}
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="aspect-video grid place-items-center text-xs text-base-content/55 p-2">
                    <ExternalLink className="size-4 opacity-50" />
                  </div>
                )}
              </a>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`¿Borrar "${a.filename}"?`)) del.mutate(a.id);
                }}
                className="absolute top-1 right-1 btn btn-circle btn-xs bg-base-100/90 text-error border-0"
                aria-label="Borrar"
              >
                <X className="size-3" />
              </button>
              <div className="px-1.5 py-0.5 text-[10px] truncate text-base-content/55">
                {a.filename}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OccurrencesBar({
  past,
  upcoming,
  selectedDate,
  onSelect,
}: {
  past: ListItem[];
  upcoming: ListItem[];
  selectedDate: string | null;
  onSelect: (d: string) => void;
}) {
  // Tab inicial: donde caiga la selección actual. Si no hay selección,
  // default a Próximas (uso más común: preparar la sesión que viene).
  // Si la serie ya acabó y no hay próximas, caemos a Pasadas.
  const initialTab: "past" | "upcoming" =
    selectedDate && past.some((i) => i.occurrenceDate === selectedDate)
      ? "past"
      : upcoming.length > 0
        ? "upcoming"
        : "past";
  const [tab, setTab] = useState<"past" | "upcoming">(initialTab);

  const list = tab === "past" ? past : upcoming;
  const empty = list.length === 0;

  // Solo mostramos las 4 más cercanas en línea. Para series largas (25+
  // standups), el "Ver todas" abre un modal con buscador y lista
  // completa — el scroll horizontal inline se vuelve incómodo a más de
  // ~8 chips.
  const COLLAPSED_LIMIT = 4;
  const visible = list.slice(0, COLLAPSED_LIMIT);
  const hiddenCount = list.length - visible.length;

  const dialogRef = useRef<HTMLDialogElement>(null);

  return (
    <div className="card-elevated p-2">
      <div className="flex items-center gap-3 flex-wrap">
        <div role="tablist" className="tabs tabs-boxed bg-base-200/60 shrink-0">
          <button
            type="button"
            role="tab"
            onClick={() => setTab("upcoming")}
            className={"tab tab-sm " + (tab === "upcoming" ? "tab-active" : "")}
          >
            Próximas
            <span className="ml-1.5 text-[10px] opacity-60">{upcoming.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            onClick={() => setTab("past")}
            className={"tab tab-sm " + (tab === "past" ? "tab-active" : "")}
          >
            Pasadas
            <span className="ml-1.5 text-[10px] opacity-60">{past.length}</span>
          </button>
        </div>

        {/* Chips horizontales. Overflow-x para que en móvil scrollee
            lateralmente sin romper el layout vertical. */}
        <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto py-0.5">
          {empty ? (
            <span className="text-xs text-base-content/45 italic px-2">
              {tab === "past"
                ? "Aún no hay sesiones pasadas."
                : "No hay próximas ocurrencias en la ventana."}
            </span>
          ) : (
            <>
              {visible.map((it) => {
                const isSelected = it.occurrenceDate === selectedDate;
                return (
                  <button
                    key={it.occurrenceDate}
                    type="button"
                    onClick={() => onSelect(it.occurrenceDate)}
                    className={
                      "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm border transition-colors " +
                      (isSelected
                        ? "bg-primary/15 border-primary/40 text-primary font-medium"
                        : "bg-base-100 border-base-300/60 hover:bg-base-200/60")
                    }
                  >
                    <Calendar
                      className={
                        "size-3.5 " + (it.isToday ? "text-primary" : "text-base-content/40")
                      }
                    />
                    <span>{formatDate(it.occurrenceDate)}</span>
                    {it.isToday && <span className="text-xs text-primary font-semibold">hoy</span>}
                    <span
                      className={`badge badge-xs ${STATUS_BADGE[it.status]}`}
                      title={STATUS_LABEL[it.status]}
                    >
                      {STATUS_LABEL[it.status]}
                    </span>
                  </button>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => dialogRef.current?.showModal()}
                  className="shrink-0 px-2.5 py-1.5 rounded-md text-xs text-primary hover:bg-base-200/60 font-medium"
                >
                  Ver todas ({list.length})
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <AllOccurrencesDialog
        ref={dialogRef}
        past={past}
        upcoming={upcoming}
        initialTab={tab}
        selectedDate={selectedDate}
        onSelect={(d) => {
          onSelect(d);
          dialogRef.current?.close();
        }}
      />
    </div>
  );
}

const AllOccurrencesDialog = forwardRef<
  HTMLDialogElement,
  {
    past: ListItem[];
    upcoming: ListItem[];
    initialTab: "past" | "upcoming";
    selectedDate: string | null;
    onSelect: (d: string) => void;
  }
>(function AllOccurrencesDialog({ past, upcoming, initialTab, selectedDate, onSelect }, ref) {
  // El tab inicial se sincroniza al `tab` del bar (lo que el user estaba
  // viendo cuando abrió el modal). No reseteamos al cerrar — si reabre
  // probablemente quiere lo mismo.
  const [tab, setTab] = useState<"past" | "upcoming">(initialTab);
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const list = tab === "past" ? past : upcoming;

  return (
    <dialog ref={ref} className="modal">
      <div className="modal-box card-elevated max-w-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-display font-semibold">Todas las ocurrencias</h3>
          <form method="dialog">
            <button className="btn btn-ghost btn-sm btn-square" aria-label="Cerrar">
              <X className="size-4" />
            </button>
          </form>
        </div>

        <div role="tablist" className="tabs tabs-boxed bg-base-200/60 mb-3">
          <button
            type="button"
            role="tab"
            onClick={() => setTab("upcoming")}
            className={"tab tab-sm " + (tab === "upcoming" ? "tab-active" : "")}
          >
            Próximas
            <span className="ml-1.5 text-[10px] opacity-60">{upcoming.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            onClick={() => setTab("past")}
            className={"tab tab-sm " + (tab === "past" ? "tab-active" : "")}
          >
            Pasadas
            <span className="ml-1.5 text-[10px] opacity-60">{past.length}</span>
          </button>
        </div>

        <ul className="max-h-[60dvh] overflow-y-auto -mx-2 divide-y divide-base-300/40">
          {list.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-base-content/55 italic">
              {tab === "past"
                ? "Aún no hay sesiones pasadas."
                : "No hay próximas ocurrencias en la ventana."}
            </li>
          ) : (
            list.map((it) => {
              const isSelected = it.occurrenceDate === selectedDate;
              return (
                <li key={it.occurrenceDate}>
                  <button
                    type="button"
                    onClick={() => onSelect(it.occurrenceDate)}
                    className={
                      "w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors rounded-md " +
                      (isSelected ? "bg-primary/10" : "hover:bg-base-200/60")
                    }
                  >
                    <Calendar
                      className={
                        "size-4 shrink-0 " + (it.isToday ? "text-primary" : "text-base-content/40")
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">
                        {formatDate(it.occurrenceDate)}
                        {it.isToday && <span className="ml-1.5 text-xs text-primary">· hoy</span>}
                      </div>
                    </div>
                    <span className={`badge badge-sm ${STATUS_BADGE[it.status]}`}>
                      {STATUS_LABEL[it.status]}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>cerrar</button>
      </form>
    </dialog>
  );
});

/**
 * Si el usuario pegó un `<iframe src="...">` completo, extrae el src.
 * Si pegó una URL directa, la devuelve tal cual (asumiendo es un embed
 * válido). Devuelve null para input vacío.
 */
function extractIframeSrc(input: string): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  // Caso iframe completo: buscar src="..." o src='...'.
  if (trimmed.startsWith("<iframe") || trimmed.includes("<iframe")) {
    const match = trimmed.match(/src=["']([^"']+)["']/i);
    return match?.[1] ?? null;
  }
  // Caso URL directa: validamos mínimo que parezca URL.
  if (trimmed.startsWith("http")) return trimmed;
  return null;
}

/**
 * Convierte una URL pública de Mural a su URL de embed iframe. Mural
 * expone `/app.mural.co/embed/...` para incrustación pública. Si no
 * matchea el patrón, devolvemos null (no se muestra preview).
 *
 * NOTA: hoy Mural usa IDs distintos entre share URL y embed URL, por lo
 * que esta función rara vez genera un embed válido. Se mantiene como
 * fallback para URLs viejas; lo recomendable es que el usuario pegue el
 * `<iframe>` o el embed URL en el campo `muralEmbedUrl`.
 */
function toMuralEmbed(url: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("mural.co")) return null;
    // Path típico: /t/<workspace>/m/<workspace>/<muralId>/...
    // El embed URL: https://app.mural.co/embed/{workspace}/{muralId}
    const parts = u.pathname.split("/").filter(Boolean);
    const tIdx = parts.indexOf("t");
    const mIdx = parts.indexOf("m");
    if (tIdx >= 0 && mIdx >= 0 && parts[mIdx + 2]) {
      const workspace = parts[mIdx + 1];
      const muralId = parts[mIdx + 2];
      return `https://app.mural.co/embed/${workspace}/${muralId}`;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Convierte una URL de Google Docs / Drive a su versión embebible
 * (`/preview`). Si la URL no matchea ningún patrón conocido, devuelve
 * la original — el iframe puede fallar pero el botón "Abrir en pestaña"
 * cubre el caso degradado.
 */
function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    // Google Docs: docs.google.com/document/d/{id}/edit → /preview.
    // Preservamos `?tab=t.xxx` porque el Gemini doc tiene tabs internas
    // (Las notas / Transcripción) y sin ese param el iframe muestra
    // siempre la tab default en vez de la solicitada.
    const docMatch = u.pathname.match(/^\/document\/d\/([^/]+)/);
    if (u.hostname === "docs.google.com" && docMatch) {
      const tab = u.searchParams.get("tab");
      return `https://docs.google.com/document/d/${docMatch[1]}/preview${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`;
    }
    // Drive file: drive.google.com/file/d/{id}/view → /preview
    const driveMatch = u.pathname.match(/^\/file\/d\/([^/]+)/);
    if (u.hostname === "drive.google.com" && driveMatch) {
      return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * Tabs con iframes para Recording / Transcription / Gemini notes. Cada
 * tab solo se muestra si el URL existe. Header de tab con icono + label;
 * arriba a la derecha botón "Abrir en pestaña" como fallback (en caso
 * de que el iframe no cargue, ej. permisos restringidos del Drive).
 */
function ArtifactsTabs({
  recordingUrl,
  transcriptUrl,
  geminiNotesUrl,
  compact = false,
}: {
  recordingUrl: string;
  transcriptUrl: string;
  geminiNotesUrl: string;
  /** Cuando se renderiza al lado del iframe de Mural (2 columnas),
   *  bajamos la altura para que ambos coincidan visualmente. */
  compact?: boolean;
}) {
  const tabs = [
    geminiNotesUrl && {
      key: "gemini",
      label: "Notas (Gemini)",
      url: geminiNotesUrl,
      Icon: Sparkles,
    },
    transcriptUrl && {
      key: "transcript",
      label: "Transcripción",
      url: transcriptUrl,
      Icon: FileText,
    },
    recordingUrl && {
      key: "recording",
      label: "Grabación",
      url: recordingUrl,
      Icon: Video,
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    url: string;
    Icon: typeof Sparkles;
  }>;
  // Primer tab activo por defecto. Si cambia la lista (ej. llega
  // recording después), conservamos el activo si todavía existe.
  const [activeKey, setActiveKey] = useState<string>(tabs[0]?.key ?? "");
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  if (!active) return null;
  return (
    <section className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-base-content/65">
        Artefactos de Meet
      </div>
      <div className="card-elevated overflow-hidden flex flex-col">
        <div
          role="tablist"
          className="flex items-center gap-1 px-2 pt-2 border-b border-base-300/60 overflow-x-auto"
        >
          {tabs.map((t) => {
            const isActive = t.key === active.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                onClick={() => setActiveKey(t.key)}
                className={
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors shrink-0 " +
                  (isActive
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-base-content/65 hover:text-base-content hover:bg-base-200/40")
                }
              >
                <t.Icon className="size-3.5" />
                {t.label}
              </button>
            );
          })}
          <a
            href={active.url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto btn btn-ghost btn-xs gap-1 shrink-0"
            title="Abrir en pestaña nueva"
          >
            <ExternalLink className="size-3" />
          </a>
        </div>
        <iframe
          key={active.key /* force reload when switching tab */}
          src={toEmbedUrl(active.url)}
          title={active.label}
          className="w-full bg-base-200/30"
          style={compact ? { height: "520px" } : { height: "70vh", minHeight: 480 }}
          allow="autoplay"
        />
      </div>
    </section>
  );
}
